import React, { useState, useCallback } from 'react';
import { Upload, FileText, AlertCircle, X, Loader2, FileSearch, Image as ImageIcon, Zap, Layers } from 'lucide-react';
import { Card } from '../ui/card';
import { useSidebar } from '../../contexts/SidebarContext';
import ChatView, { GEMINI_CHAT_MODELS } from '../chat/ChatView';
import { parseGaebXml, serializeForChat } from '../../services/gaebParser';

const PDF_PROCESSOR_URL = process.env.REACT_APP_PDF_PROCESSOR_URL || 'http://localhost:8000';
const MAX_FILES = 5;

const isGaebFile = (name) => /\.(x83|xml)$/i.test(name);
const isBinaryDoc = (name) => /\.(docx|pdf|xlsx)$/i.test(name);
const isExcelFile = (name) => /\.xlsx$/i.test(name);

const readFileAsText = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = (e) => resolve(e.target.result);
  reader.onerror = () => reject(reader.error);
  reader.readAsText(file, 'UTF-8');
});

const gaebFileToText = async (file) => {
  const xml = await readFileAsText(file);
  const parsed = parseGaebXml(xml);
  const s = serializeForChat(parsed);
  return (
    `Projekt: ${s.projectLabel} (Nr: ${s.projectName})\n` +
    `Auftraggeber: ${s.ownerName} — ${s.ownerAddress}\n` +
    `LV: ${s.boqLabel} | Datum: ${s.date} | ${s.awardCategory} | Währung: ${s.currency}\n\n` +
    `=== LEISTUNGSVERZEICHNIS ===\n${s.boqSummary}`
  );
};

const MultiFileUploadArea = ({ onSubmit, isUploading }) => {
  const [dragOver, setDragOver] = useState(false);
  const [files, setFiles] = useState([]);

  const addFiles = (newFiles) => {
    const accepted = Array.from(newFiles).filter(f => isBinaryDoc(f.name) || isGaebFile(f.name));
    setFiles(prev => [...prev, ...accepted].slice(0, MAX_FILES));
  };

  const removeAt = (idx) => setFiles(prev => prev.filter((_, i) => i !== idx));

  return (
    <Card className="p-6">
      <div
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragOver(false);
          addFiles(e.dataTransfer.files);
        }}
        className={`border-2 border-dashed rounded-lg p-10 text-center transition-colors cursor-pointer ${
          dragOver ? 'border-green-500 bg-green-50' : 'border-gray-300 hover:border-green-400 hover:bg-gray-50'
        }`}
        onClick={() => document.getElementById('docx-input').click()}
      >
        <input
          id="docx-input"
          type="file"
          accept=".docx,.pdf,.xlsx,.X83,.x83,.xml"
          multiple
          className="hidden"
          onChange={(e) => addFiles(e.target.files)}
        />
        <Upload className={`h-10 w-10 mx-auto mb-3 ${dragOver ? 'text-green-500' : 'text-gray-400'}`} />
        <p className="text-sm font-medium text-gray-700">Word-, PDF-, Excel- oder GAEB-Dokumente (.docx, .pdf, .xlsx, .X83, .xml) hochladen</p>
        <p className="text-xs text-gray-500 mt-1">Drag & Drop oder klicken · max. {MAX_FILES} Dateien</p>
      </div>

      {files.length > 0 && (
        <div className="mt-4 space-y-2">
          {files.map((f, i) => (
            <div key={`${f.name}-${i}`} className="flex items-center justify-between bg-gray-50 rounded px-3 py-2 text-sm">
              <div className="flex items-center gap-2 min-w-0">
                {isGaebFile(f.name)
                  ? <Layers className="h-4 w-4 text-amber-600 flex-shrink-0" />
                  : <FileText className={`h-4 w-4 flex-shrink-0 ${isExcelFile(f.name) ? 'text-emerald-600' : 'text-green-600'}`} />}
                <span className="truncate text-gray-800">{f.name}</span>
                {isGaebFile(f.name) && <span className="text-[10px] uppercase tracking-wide bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded flex-shrink-0">GAEB</span>}
                {isExcelFile(f.name) && <span className="text-[10px] uppercase tracking-wide bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded flex-shrink-0">XLSX</span>}
                <span className="text-xs text-gray-400 flex-shrink-0">({(f.size / 1024).toFixed(0)} KB)</span>
              </div>
              <button
                onClick={() => removeAt(i)}
                className="text-gray-400 hover:text-red-500 flex-shrink-0"
                disabled={isUploading}
                aria-label={`${f.name} entfernen`}
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          ))}
          <button
            onClick={() => onSubmit(files)}
            disabled={isUploading || files.length === 0}
            className="w-full mt-3 px-4 py-2.5 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium flex items-center justify-center gap-2 transition-colors"
          >
            {isUploading ? (
              <><Loader2 className="h-4 w-4 animate-spin" /> {files.length} Datei{files.length > 1 ? 'en werden' : ' wird'} verarbeitet...</>
            ) : (
              `${files.length} Datei${files.length > 1 ? 'en' : ''} verarbeiten`
            )}
          </button>
        </div>
      )}
    </Card>
  );
};

export const Dokumente = () => {
  const { isExpanded } = useSidebar();
  const leftMargin = isExpanded ? 'ml-64' : 'ml-16';

  const [sessionId, setSessionId] = useState(null);
  const [sessionDocs, setSessionDocs] = useState([]);
  const [sessionMeta, setSessionMeta] = useState({ total_tokens: 0, total_images: 0, cached: false });
  const [sessionError, setSessionError] = useState(null);
  const [isUploading, setIsUploading] = useState(false);

  const handleSubmit = useCallback(async (files) => {
    setIsUploading(true);
    setSessionError(null);
    try {
      const formData = new FormData();
      const gaebTexts = [];
      for (const f of files) {
        if (isGaebFile(f.name)) {
          try {
            const content = await gaebFileToText(f);
            gaebTexts.push({ name: f.name, content });
          } catch (err) {
            throw new Error(`GAEB-Datei ${f.name} konnte nicht gelesen werden: ${err.message}`);
          }
        } else {
          formData.append('files', f);
        }
      }
      if (gaebTexts.length > 0) {
        formData.append('texts', JSON.stringify(gaebTexts));
      }
      const res = await fetch(`${PDF_PROCESSOR_URL}/api/docs/session`, {
        method: 'POST',
        body: formData,
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `Server error ${res.status}`);
      }
      const data = await res.json();
      setSessionId(data.session_id);
      setSessionDocs(data.docs);
      setSessionMeta({
        total_tokens: data.total_tokens || 0,
        total_images: data.total_images || 0,
        cached: Boolean(data.cached),
      });
    } catch (err) {
      setSessionError(err.message);
    } finally {
      setIsUploading(false);
    }
  }, []);

  const reset = () => {
    setSessionId(null);
    setSessionDocs([]);
    setSessionMeta({ total_tokens: 0, total_images: 0, cached: false });
    setSessionError(null);
  };

  return (
    <div className={`${leftMargin} h-screen bg-gray-50 transition-all duration-300 flex flex-col overflow-hidden`}>
      <div className="p-8 flex-1 min-h-0 flex flex-col">
        <div className="flex items-center gap-3 mb-6 flex-shrink-0">
          <FileSearch className="h-7 w-7 text-green-600" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Dokumente</h1>
            <p className="text-sm text-gray-500">Word-, PDF-, Excel- oder GAEB-Dateien (.docx, .pdf, .xlsx, .X83, .xml) hochladen und Fragen stellen</p>
          </div>
        </div>

        {!sessionId && (
          <div className="flex-shrink-0">
            <MultiFileUploadArea onSubmit={handleSubmit} isUploading={isUploading} />
            {sessionError && (
              <Card className="p-4 mt-4 border-red-200 bg-red-50">
                <div className="flex items-start gap-2">
                  <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-red-700">{sessionError}</p>
                </div>
              </Card>
            )}
          </div>
        )}

        {sessionId && (
          <>
            <Card className="px-4 py-3 mb-4 flex-shrink-0">
              <div className="flex items-center justify-between gap-4">
                <div className="flex items-center gap-2 flex-wrap min-w-0">
                  {sessionDocs.map((d, i) => (
                    <div key={i} className="flex items-center gap-1.5 bg-green-50 px-2 py-1 rounded text-xs">
                      <FileText className="h-3.5 w-3.5 text-green-600 flex-shrink-0" />
                      <span className="text-gray-700 truncate max-w-[240px]">{d.name}</span>
                      <span className="text-gray-400">~{Math.round(d.tokens / 1000)}k tok</span>
                      {d.image_count > 0 && (
                        <span className="flex items-center gap-0.5 text-gray-400">
                          <ImageIcon className="h-3 w-3" />{d.image_count}
                        </span>
                      )}
                    </div>
                  ))}
                  <span className="text-xs text-gray-500 ml-1">
                    gesamt: ~{Math.round(sessionMeta.total_tokens / 1000)}k Tokens
                    {sessionMeta.total_images > 0 && ` · ${sessionMeta.total_images} Bilder`}
                  </span>
                  {sessionMeta.cached && (
                    <span className="flex items-center gap-1 text-xs text-green-700 bg-green-100 px-2 py-0.5 rounded" title="Kontext gecacht — schneller & günstiger pro Frage">
                      <Zap className="h-3 w-3" />cached
                    </span>
                  )}
                </div>
                <button
                  onClick={reset}
                  className="text-sm text-green-600 hover:text-green-700 font-medium flex-shrink-0"
                >
                  Neue Dateien
                </button>
              </div>
            </Card>
            <Card className="overflow-hidden flex-1 min-h-0 flex flex-col">
              <ChatView
                sessionId={sessionId}
                sessionError={null}
                chatUrl={`${PDF_PROCESSOR_URL}/api/docs/chat`}
                emptyHint="Fragen zu den Dokumenten?"
                inputPlaceholder="Frage zu den Dokumenten stellen..."
                availableModels={GEMINI_CHAT_MODELS}
                modelStorageKey="docsChatModelId"
                onResponse={(d) => {
                  if (d?.cached) setSessionMeta(m => ({ ...m, cached: true }));
                }}
              />
            </Card>
          </>
        )}
      </div>
    </div>
  );
};

export default Dokumente;
